import {
  forwardRef,
  useCallback,
  ComponentType,
  ForwardedRef,
  KeyboardEvent,
} from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useDeepCompareMemo } from '@blumintinc/use-deep-compare';
import { memo } from '../../util/memo';

export const DEFAULT_DEFOCUS_KEYS = ['Enter', 'Tab', 'Escape'] as const;

export type WithOnDefocusProps = {
  onKeyDown?: (event: KeyboardEvent) => void;
};

// eslint-disable-next-line @blumintinc/blumint/prefer-settings-object
export function withOnDefocus<TProps extends WithOnDefocusProps>(
  WrappedComponent: ComponentType<TProps>,
  onDefocus: () => void,
  keys: readonly string[] = DEFAULT_DEFOCUS_KEYS,
) {
  const WithOnDefocusUnmemoized = forwardRef(function WithOnDefocus(
    { onKeyDown, ...props }: TProps,
    ref: ForwardedRef<unknown>,
  ) {
    const interceptKeydown = useCallback(
      (event: React.KeyboardEvent) => {
        if (keys.includes(event.key)) {
          event.preventDefault();
          onDefocus();
        }
        onKeyDown?.(event);
      },
      [onKeyDown],
    );

    const wrappedComponent = useDeepCompareMemo(() => {
      return (
        <WrappedComponent
          // eslint-disable-next-line @blumintinc/blumint/no-type-assertion-returns
          {...(props as TProps)}
          ref={ref}
          onKeyDown={interceptKeydown}
        />
      );
    }, [interceptKeydown, props, ref]);

    return (
      /**
       * The <Select /> component renders its menu via a modal that blocks the rest of the app, so any click is captured within this modal and
       * not considered a "click away". Although the portaled menu is placed outside the wrapped component's DOM subtree, it still belongs
       * to the same React tree. Therefore, we disableReactTree to ensure that the onClickAway is correctly triggered when clicking on the portaled menu.
       * */
      <ClickAwayListener disableReactTree onClickAway={onDefocus}>
        <div>{wrappedComponent}</div>
      </ClickAwayListener>
    );
  });

  return memo(WithOnDefocusUnmemoized);
}
